import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import {
  encryptString,
  createSession,
  getAbhaProfile,
  verifyUser,
} from "../../services/apiUtils";
import Loader from "../../components/common/Loader";
import { ToastContainer, toast } from "react-toastify";
import authService from "../../../Services/authService";
import AadharConsentSection from "../common/AadharConsentSection";

const ForgoatAadharNumber = () => {
  const [aadhaar, setAadhaar] = useState("");
  const [step, setStep] = useState("aadhaar");
  const [otpTextValue, setOtpTextValue] = useState("");
  const [txnId, setTxnId] = useState("");
  const [resendTimer, setResendTimer] = useState("");
  const [apiResponse, setApiResponse] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaSessionId, setCaptchaSessionId] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [showAadhaar, setShowAadhaar] = useState(false);
  const [formatedAadhaar, setFormatedAadhaar] = useState("");
  const [resendCount, setResendCount] = useState(1);
  const [aadharError, setAadharError] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [consent3, setConsent3] = useState(false);
  const [consent4, setConsent4] = useState(false);
  const [consent5, setConsent5] = useState(false);
  const [consent6, setConsent6] = useState(false);
  const [consent7, setConsent7] = useState(false);
  const [healthcareWorkerName, setHealthcareWorkerName] = useState(() => {
    try {
      const userData = authService.getUser();
      return (
        userData?.name ||
        userData?.username ||
        userData?.email?.split("@")[0] ||
        "Healthcare Worker"
      );
    } catch {
      return "Healthcare Worker";
    }
  });
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [acceptAll, setAcceptAll] = useState(false);

  const navigate = useNavigate();
  const back = () => {
    setResendCount(1);
    navigate("/abha-home");
  };

  // Handle Accept All checkbox
  const handleAcceptAll = (checked) => {
    setAcceptAll(checked);
    setConsent1(checked);
    setConsent2(checked);
    setConsent3(checked);
    setConsent4(checked);
    setConsent5(checked);
    setConsent6(checked);
    setConsent7(checked);
  };

  // Update Accept All when individual consents change
  useEffect(() => {
    const allChecked =
      consent1 &&
      consent2 &&
      consent3 &&
      consent4 &&
      consent5 &&
      consent6 &&
      consent7;
    setAcceptAll(allChecked);
  }, [consent1, consent2, consent3, consent4, consent5, consent6, consent7]);

  // Check if all required consents are checked
  const areAllConsentsChecked = () => {
    return (
      consent1 &&
      consent2 &&
      consent3 &&
      consent4 &&
      consent5 &&
      consent6 &&
      consent7
    );
  };

  // Generate captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/abha/captcha/generate`);
      if (response.data.success) {
        setCaptchaSessionId(response.data.data.sessionId);
        setCaptchaImage(response.data.data.captchaImage);
        setCaptchaInput("");
      } else {
        toast.error("Failed to generate captcha. Please try again.");
      }
    } catch (error) {
      console.error("Error generating captcha:", error);
      toast.error("Failed to generate captcha. Please try again.");
    }
  };

  const handleFormateAadhaar = (e) => {
    const inputValue = e.target.value;

    if (showAadhaar) {
      // When visible, format with dashes
      const numericValue = inputValue.replace(/[^0-9]/g, "");
      const limitedValue = numericValue.slice(0, 12);
      setAadhaar(limitedValue);

      let formatted = limitedValue;
      if (limitedValue.length > 4) {
        formatted = limitedValue.slice(0, 4) + "-" + limitedValue.slice(4);
      }
      if (limitedValue.length > 8) {
        formatted =
          limitedValue.slice(0, 4) +
          "-" +
          limitedValue.slice(4, 8) +
          "-" +
          limitedValue.slice(8);
      }
      setFormatedAadhaar(formatted);

      // Validate Aadhaar
      if (limitedValue.length === 0) {
        setAadharError("Aadhaar number is required");
      } else if (limitedValue.length !== 12) {
        setAadharError("Aadhaar number must be 12 digits");
      } else {
        setAadharError("");
      }
    } else {
      // When masked, compare display lengths
      const currentLength = formatedAadhaar.length;
      const newLength = inputValue.length;

      if (newLength > currentLength) {
        // User is adding a digit
        const newDigit = inputValue.replace(/[^0-9]/g, "").slice(-1);
        if (newDigit && aadhaar.length < 12) {
          const updatedValue = aadhaar + newDigit;
          setAadhaar(updatedValue);

          const stars = updatedValue
            .split("")
            .map(() => "*")
            .join("");
          let masked = "";
          if (updatedValue.length <= 4) {
            masked = stars;
          } else if (updatedValue.length <= 8) {
            masked = stars.slice(0, 4) + "-" + stars.slice(4);
          } else {
            masked =
              stars.slice(0, 4) +
              "-" +
              stars.slice(4, 8) +
              "-" +
              stars.slice(8);
          }
          setFormatedAadhaar(masked);
        }
      } else if (newLength < currentLength) {
        // User is removing a digit (backspace)
        const updatedValue = aadhaar.slice(0, -1);
        setAadhaar(updatedValue);

        const stars = updatedValue
          .split("")
          .map(() => "*")
          .join("");
        let masked = "";
        if (updatedValue.length <= 4) {
          masked = stars;
        } else if (updatedValue.length <= 8) {
          masked = stars.slice(0, 4) + "-" + stars.slice(4);
        } else {
          masked =
            stars.slice(0, 4) + "-" + stars.slice(4, 8) + "-" + stars.slice(8);
        }
        setFormatedAadhaar(masked);
      }

      // Validate Aadhaar for masked input
      const currentAadharLength =
        aadhaar.length + (newLength > currentLength ? 1 : -1);
      if (currentAadharLength === 0) {
        setAadharError("Aadhaar number is required");
      } else if (currentAadharLength !== 12) {
        setAadharError("Aadhaar number must be 12 digits");
      } else {
        setAadharError("");
      }
    }
  };

  const toggleAadhaarVisibility = () => {
    setShowAadhaar(!showAadhaar);
    if (!showAadhaar && aadhaar) {
      // Switching to visible (unmasked)
      let formatted = aadhaar;
      if (aadhaar.length > 4) {
        formatted = aadhaar.slice(0, 4) + "-" + aadhaar.slice(4);
      }
      if (aadhaar.length > 8) {
        formatted =
          aadhaar.slice(0, 4) +
          "-" +
          aadhaar.slice(4, 8) +
          "-" +
          aadhaar.slice(8);
      }
      setFormatedAadhaar(formatted);
    } else if (aadhaar) {
      // Switching to masked
      const stars = aadhaar
        .split("")
        .map(() => "*")
        .join("");
      let masked = "";
      if (aadhaar.length <= 4) {
        masked = stars;
      } else if (aadhaar.length <= 8) {
        masked = stars.slice(0, 4) + "-" + stars.slice(4);
      } else {
        masked =
          stars.slice(0, 4) + "-" + stars.slice(4, 8) + "-" + stars.slice(8);
      }
      setFormatedAadhaar(masked);
    }
  };

  const handleSendOtp = async () => {
    if (!aadhaar || aadhaar.length !== 12) {
      toast.error("Please enter valid Aadhaar number (12 digits)");
      return;
    }
    if (!beneficiaryName || beneficiaryName.trim() === "") {
      toast.error("Please enter beneficiary name.");
      return;
    }
    if (!areAllConsentsChecked()) {
      toast.error("Please accept the required terms and conditions");
      return;
    }

    // Validate captcha with backend
    if (!captchaInput || captchaInput.trim() === "") {
      toast.error("Please enter the captcha.");
      return;
    }

    try {
      // Verify captcha with backend
      const captchaResponse = await axios.post(
        `${API_BASE_URL}/abha/captcha/validate`,
        {
          sessionId: captchaSessionId,
          captcha: captchaInput,
        },
      );

      if (!captchaResponse.data.success || !captchaResponse.data.valid) {
        toast.error("Invalid captcha. Please try again.");
        generateCaptcha(); // Regenerate captcha
        setCaptchaInput(""); // Clear input
        return;
      }
    } catch (error) {
      toast.error("Failed to validate captcha. Please try again.");
      generateCaptcha();
      setCaptchaInput("");
      return;
    }

    try {
      setIsPageLoading(true);

      const encryptedAadhaar = await encryptString(aadhaar);
      // const sessionResponse = await createSession();
      //  const accessToken = sessionResponse?.accessToken;

      // if (!accessToken) {
      //   throw new Error("Access token not received from session");
      // }

      // setAccessToken(accessToken);

      const response = await axios.post(
        `${API_BASE_URL}/abha/forgot/aadhar/send-otp`,
        {
          loginId: encryptedAadhaar,
        },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: `${accessToken}`,
          },
        },
      );

      const dtresponse = response.data;

      if (response.status === 200) {
        setTxnId(dtresponse.data.txnId);
        setStep("otp");
        toast.success(dtresponse?.data?.message);
        setResendTimer(60);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error.loginId ||
          "Invalid Aadhaar number. Please try again.",
      );
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(timer); // Cleanup timer on unmount
    }
  }, [resendTimer]);

  const handleResendOtp = async () => {
    try {
      setIsPageLoading(true);
      const encryptedAadhaar = await encryptString(aadhaar);
      // const sessionResponse = await createSession();
      // const token = sessionResponse?.accessToken;

      // if (!token) {
      //   throw new Error("Access token not received from session");
      // }

      // setAccessToken(token);

      const response = await axios.post(
        `${API_BASE_URL}/abha/forgot/aadhar/send-otp`,
        { loginId: encryptedAadhaar },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: `${token}`,
          },
        },
      );

      if (response.status === 200) {
        setTxnId(response.data.data.txnId);
        toast.success(
          response.data?.data?.message || "OTP resent successfully.",
        );
        setResendTimer(60);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Server error occurred. Please try again later.");
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    // Allow only digits and up to 6 characters
    if (/^\d{0,6}$/.test(value)) {
      setOtpTextValue(value);
      // Validate OTP
      if (value.length === 0) {
        setOtpError("OTP is required");
      } else if (value.length < 6) {
        setOtpError("OTP must be 6 digits");
      } else {
        setOtpError("");
      }
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setIsPageLoading(true);
      const encryptedOtp = await encryptString(otpTextValue);

      if (!encryptedOtp) {
        toast.error("Failed to encrypt OTP. Please try again.");
        setIsPageLoading(false);
        return;
      }

      // Use the stored access token or get a new one if needed
      // const sessionResponse = await createSession();
      // const token = sessionResponse?.accessToken;

      const response = await axios.post(
        `${API_BASE_URL}/abha/forgot/aadhar/verify-otp`,
        {
          txnId: txnId,
          otp: encryptedOtp,
        },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: `${token || accessToken}`,
          },
        },
      );

      const dtresponse = response.data;

      if (dtresponse.success) {
        if (dtresponse.data.authResult === "success") {
          toast.success(dtresponse?.data?.message);
          setApiResponse(dtresponse.data);

          // Store x_token if available
          if (dtresponse.data?.token) {
            // localStorage.setItem("x_token", dtresponse.data.token);

            // Get ABHA profile data
            // Cookie is set by verify-otp — call profile directly
            const abhaProfileData = await getAbhaProfile();

            if (abhaProfileData?.success) {
              localStorage.setItem(
                "orignalBeneficiaryData",
                JSON.stringify(abhaProfileData?.data),
              );
              localStorage.setItem(
                "abhaProfileData",
                JSON.stringify(abhaProfileData),
              );
              setIsPageLoading(false);
              navigate("/abha-Card");
            } else {
              toast.error(
                abhaProfileData?.message ||
                  "Failed to fetch ABHA profile data. Please try again.",
              );
              setIsPageLoading(false);
              return;
            }
          } else {
            toast.error("Token not received. Please try again.");
            setIsPageLoading(false);
            return;
          }
        } else {
          toast.error(
            dtresponse?.data?.message || "Wrong OTP. Please try again.",
          );
          setIsPageLoading(false);
          return;
        }
      } else {
        toast.error(
          dtresponse?.message || "Failed to verify OTP. Please try again.",
        );
        setIsPageLoading(false);
      }
    } catch (err) {
      setIsPageLoading(false);
      console.error(err);
      if (
        err.response?.data?.success === false &&
        err.response?.data?.message === "Unclassified Authentication Failure"
      ) {
        toast.error(err.response.data.message + ". Please try again!");
      } else {
        toast.error(
          err.response?.data?.message ||
            "Failed to verify OTP. Please try again.",
        );
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      {isPageLoading && <Loader />}
      <div className="flow-feilds aadharCardLogin">
        {step === "aadhaar" && (
          <>
            <div className="feilds">
              <label>
                Aadhar Number <span style={{ color: "red" }}>*</span>
              </label>
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type="text"
                  className="filds-form"
                  placeholder="XXXX-XXXX-XXXX"
                  value={formatedAadhaar}
                  autoComplete="off"
                  maxLength={14}
                  onChange={handleFormateAadhaar}
                  style={{ paddingRight: "40px", width: "100%" }}
                />
                <i
                  className="material-icons"
                  onClick={toggleAadhaarVisibility}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  {showAadhaar ? "visibility" : "visibility_off"}
                </i>
              </div>
              {aadharError && (
                <span
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                    display: "block",
                  }}
                >
                  {aadharError}
                </span>
              )}
            </div>

            <div className="feilds" style={{ marginTop: "15px" }}>
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                {/* Name Input Fields */}
                <div style={{ marginBottom: "15px" }}>
                  <div className="feilds" style={{ marginBottom: "15px" }}>
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        marginBottom: "5px",
                        display: "block",
                      }}
                    >
                      Beneficiary Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="filds-form"
                      placeholder="Enter beneficiary name"
                      value={beneficiaryName}
                      onChange={(e) => setBeneficiaryName(e.target.value)}
                      style={{
                        width: "100%",
                        fontSize: "13px",
                        padding: "8px",
                      }}
                    />
                  </div>
                </div>

                <AadharConsentSection
                  acceptAll={acceptAll}
                  handleAcceptAll={handleAcceptAll}
                  consent1={consent1}
                  setConsent1={setConsent1}
                  consent2={consent2}
                  setConsent2={setConsent2}
                  consent3={consent3}
                  setConsent3={setConsent3}
                  consent4={consent4}
                  setConsent4={setConsent4}
                  consent5={consent5}
                  setConsent5={setConsent5}
                  consent6={consent6}
                  setConsent6={setConsent6}
                  consent7={consent7}
                  setConsent7={setConsent7}
                  beneficiaryName={beneficiaryName}
                  setBeneficiaryName={setBeneficiaryName}
                  healthcareWorkerName={healthcareWorkerName}
                />
              </div>
            </div>

            <div className="feilds" style={{ marginTop: "15px" }}>
              <label>
                Captcha <span style={{ color: "red" }}>*</span>
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "5px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    flex: 1,
                    textAlign: "center",
                    userSelect: "none",
                    minHeight: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {captchaImage ? (
                    <img
                      src={captchaImage}
                      alt="Captcha"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  ) : (
                    <span style={{ color: "#999" }}>Loading captcha...</span>
                  )}
                </div>
                <i
                  className="material-icons"
                  onClick={generateCaptcha}
                  style={{ cursor: "pointer", fontSize: "24px" }}
                  title="Refresh Captcha"
                >
                  refresh
                </i>
              </div>
              <input
                type="text"
                className="filds-form"
                placeholder="Enter captcha text"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                }}
                autoComplete="off"
              />
            </div>
          </>
        )}

        {step === "otp" && (
          <div className="feilds">
            <label>Enter OTP</label>
            <input
              type="text"
              className="filds-form"
              placeholder="Enter OTP"
              maxLength={6}
              value={otpTextValue}
              onChange={handleOtpChange}
              autoComplete="off"
            />
            {otpError && (
              <span
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  marginTop: "5px",
                  display: "block",
                }}
              >
                {otpError}
              </span>
            )}
            <span
              className="notes text-end"
              onClick={() => {
                if (resendTimer === 0 && resendCount < 3) {
                  setResendCount(resendCount + 1);
                  handleResendOtp();
                }
              }}
              style={{
                pointerEvents:
                  resendTimer > 0 || resendCount >= 3 ? "none" : "auto",
                color: resendTimer > 0 || resendCount >= 3 ? "#999" : "#007bff",
                cursor:
                  resendTimer > 0 || resendCount >= 3
                    ? "not-allowed"
                    : "pointer",
                display: "block",
                marginTop: "5px",
              }}
            >
              {resendTimer > 0
                ? `00:${resendTimer < 10 ? "0" + resendTimer : resendTimer} Resend OTP (${3 - resendCount} ${3 - resendCount === 1 ? "attempt" : "attempts"} left)`
                : resendCount > 3
                  ? "Maximum resend attempts reached"
                  : `Resend OTP (${3 - resendCount} ${3 - resendCount === 1 ? "attempt" : "attempts"} left)`}
            </span>
          </div>
        )}
      </div>

      <div className="flow-feilds pb-3" style={{ minHeight: "auto" }}>
        <div className="feilds feildsBtn">
          <button className="custBtn btnCancel" onClick={back}>
            Back
          </button>
          <button
            className={`custBtn ${
              step === "aadhaar"
                ? aadhaar.length === 12
                  ? "btnSuccess"
                  : "disabled"
                : step === "otp"
                  ? otpTextValue.length === 6
                    ? "btnSuccess"
                    : "disabled"
                  : "btnSuccess"
            }`}
            disabled={
              step === "aadhaar"
                ? aadhaar.length !== 12
                : step === "otp"
                  ? otpTextValue.length !== 6
                  : false
            }
            onClick={
              step === "aadhaar"
                ? handleSendOtp
                : step === "otp"
                  ? handleVerifyOtp
                  : null
            }
          >
            {step === "aadhaar"
              ? "Send OTP"
              : step === "otp"
                ? "Verify OTP"
                : "Submit"}{" "}
            <i className="material-icons">arrow_right_alt</i>
          </button>
        </div>
      </div>
    </>
  );
};

export default ForgoatAadharNumber;
